<script setup lang="ts">
import { useStorage } from '@vueuse/core'
import ElementalMainFeed from './ElementalFeed.vue'
import type { PromptMinified } from '~/types/prompt'
import type { PromptAdPlaceholder } from '~/types/ad'
import { getElementalHomeFeed } from '~/api/feed'
import type { List } from '~/types/list'
import { getElementalTypes } from '~/api/elementals'
import { ElementalTypeEnum } from '~/types/elemental'
import type { Elemental, ElementalType, ViewType } from '~/types/elemental'
import { useFilters } from '~/composables/filters'
import { getSetting } from '~~/src/types/elemental'
import { PROMPT_TYPES } from '@/utils/constants'
import { provideContainerSize } from '~/composables/useContainerSize'

const props = defineProps<{
  elementalTypeIds: ElementalTypeEnum[]
  typeIds?: PROMPT_TYPES[]
}>()

const ElementalFeed = defineAsyncComponent(() =>
  import('~~/src/components/ElementalFeed/index.vue'),
)

const CompaniesSection = defineAsyncComponent(() =>
  import('~~/src/components/CompaniesSection/index.vue'),
)

const user = useUser()
const filters = useFilters()
const featuredPrompts = useFeaturedPrompts()

const route = useRoute()
const router = useRouter()

const selectedView = ref<ViewType>('grid')

const homeList = ref<Elemental[]>([])
const pageSize = computed(() => (selectedView.value === 'grid' ? 12 : 10) * 4)
const pageNumber = ref(1)
const canLoadMore = ref(true)
const isLoading = ref(true)
const isPendingHomeListRequest = ref(false)

const hasLoadedFeatured = ref(false)

const enterprise = useEnterprise()

const allTimeList = ref<Elemental[]>([])
const willShowAllTimePrompts = ref(false)
const pageSizeAllTime = computed(() => (selectedView.value === 'grid' ? 12 : 10) * 4)
const pageNumberAllTime = ref(1)
const canLoadMoreAllTime = ref(true)
const isLoadingAllTime = ref(false)
const prompts = ref<(List | PromptMinified | PromptAdPlaceholder)[]>()

const elementalTypes = ref<ElementalType[]>()
const selectedElementalTypes = ref<ElementalTypeEnum[]>([])
const searchQuery = ref<string>()

const AVAILABLE_TYPES = props.elementalTypeIds
const promptTypeStorage = useStorage(PROMPT_TYPE_STORAGE_KEY, PROMPT_TYPE_STORAGE_DEFAULT)

const DISTANCE_TO_LOAD_MORE = 500
const containerRef = ref<HTMLFormElement>()

// Usar useElementSize apenas no cliente para evitar problemas de SSR
const { width, height } = process.client ? useElementSize(containerRef) : { width: ref(0), height: ref(0) }
const isSmall = computed(() => width.value < 780)

const viewSettings = computed(() => {
  const typeMap: Record<string, string> = {
    promptText: 'TextPrompt',
    promptVisual: 'VisualPrompt',
    automation: 'TextPrompt',
  }

  const selectedTab = 'prompt'

  let setting
  if (selectedTab === 'prompt') {
    const typeName = filters.value.type_ids?.includes(PROMPT_TYPES.Visual) ? 'Visual' : 'Text'
    setting = typeMap[`${selectedTab}${typeName}`]
  }
  else { setting = typeMap[selectedTab] }

  return getSetting(setting)
})

filters.value.type_ids = props.typeIds
selectedElementalTypes.value = []

watchDebounced([filters, user, enterprise, searchQuery, viewSettings, selectedElementalTypes], () => {
  canLoadMore.value = true

  loadHomeList(false, true)
}, { debounce: 300, deep: true })

watch(promptTypeStorage, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    if (searchQuery.value)
      searchQuery.value = undefined

    if (willShowAllTimePrompts.value) {
      canLoadMoreAllTime.value = true
      loadAllTimeFeed()
    }
  }
})

const votedPromptsUpdate = useUpdatePromptVote()
watch(votedPromptsUpdate, () => {
  prompts.value?.forEach((promptV) => {
    const prompt = promptV as PromptMinified
    if (prompt.id === votedPromptsUpdate.value?.id)
      prompt.isVoted = votedPromptsUpdate.value.state === 'added'
  })
})

async function loadHomeList(nextPage = false, clear = false) {
  if (!canLoadMore.value || isPendingHomeListRequest.value)
    return

  isLoading.value = true
  isPendingHomeListRequest.value = true
  if (clear)
    homeList.value = []

  if (nextPage)
    pageNumber.value += 1
  else
    pageNumber.value = 1

  if (!['votes', 'newest', 'saves', 'uses'].includes(filters.value.order_by))
    filters.value.order_by = 'votes'

  const hasViewFilters = viewSettings.value?.filters

  try {
    const result = await getElementalHomeFeed({
      ...filtersToQueries(filters.value),
      page: pageNumber.value,
      size: pageSize.value,
      enterprise_id: enterprise.value,
      search: searchQuery.value,
      is_premium: filters.value.is_premium || undefined,
      elemental_type_ids: selectedElementalTypes.value?.length ? selectedElementalTypes.value : props.elementalTypeIds,
      ...hasViewFilters,
      type_ids: props.typeIds,
    }) || []

    homeList.value = [
      ...((pageNumber.value > 1 && homeList.value) ? homeList.value : []),
      ...(result || []),
    ]

    if (result.length < pageSize.value) {
      canLoadMore.value = false
      if (filters.value.interval !== 0)
        await loadAllTimeFeed()
    }
  }
  catch (error) {
    if (pageNumber.value === 1)
      homeList.value = []

    console.error(error)
  }
  finally {
    willShowAllTimePrompts.value = !canLoadMore.value && filters.value.interval !== 0
    isLoading.value = false
    isPendingHomeListRequest.value = false
  }
}

async function loadAllTimeFeed(nextPage = false) {
  if (!canLoadMoreAllTime.value)
    return

  isLoadingAllTime.value = true
  if (nextPage)
    pageNumberAllTime.value += 1
  else
    pageNumberAllTime.value = 1

  const result = await getElementalHomeFeed({
    ...filtersToQueries(filters.value),
    page: pageNumberAllTime.value,
    size: pageSizeAllTime.value,
    enterprise_id: enterprise.value,
    interval: 0,
    is_premium: filters.value.is_premium || undefined,
    type_ids: props.typeIds,
    elemental_type_ids: selectedElementalTypes.value?.length ? selectedElementalTypes.value : props.elementalTypeIds,
  }) || []

  const newPromptsArray: Elemental[] = [
    ...((pageNumberAllTime.value > 1 && allTimeList.value) ? allTimeList.value : []),
    ...result,
  ]

  allTimeList.value = newPromptsArray

  if (result.length < pageSizeAllTime.value)
    canLoadMoreAllTime.value = false
  isLoadingAllTime.value = false
}

const layoutScrollHandler = () => {
  if (canLoadMore.value && !isLoading.value) {
    isLoading.value = true
    loadHomeList(true)
  }
  else if (canLoadMoreAllTime.value && willShowAllTimePrompts.value && !isLoadingAllTime.value) {
    loadAllTimeFeed(true)
  }
}

watch(viewSettings, () => {
  if (viewSettings.value)
    selectedView.value = viewSettings.value.defaultView
})

const handleLayout = (tab: ViewType) => {
  selectedView.value = tab
}

const scrollTo = (targetId: string) => {
  if (process.client) {
    const el = document.querySelector(targetId)
    const mainScroll = document.querySelector('#main-scroll')
    if (el && mainScroll)
      mainScroll.scrollTo({ top: el.getClientRects()[0].top - 80, behavior: 'smooth' })
  }
}

const showAutomationFeatured = computed(() => route.path === '/automations' || route.path === '/')
const showPromptFeatured = computed(() => route.path === '/prompts' || route.path === '/')
const showPromptVisualFeatured = computed(() => route.path === '/visual-prompts' || route.path === '/')

const handleExplore = (goto?: string) => {
  if (route.path === '/')
    return router.push(`/${goto}`)

  else if (route.path === '/automations' && goto === 'automations')
    return scrollTo('#automations')

  else if (route.path === '/prompts' && goto === 'prompts')
    return scrollTo('#prompts')
}

onMounted(async () => {
  if (!['votes', 'newest', 'saves', 'uses'].includes(filters.value.order_by))
    filters.value.order_by = 'votes'

  try {
    elementalTypes.value = await getElementalTypes() || []
  }
  catch (error) {
    console.warn('Failed to load elemental types:', error)
    elementalTypes.value = []
  }

  featuredPrompts.value = {
    usedPrompts: [],
    prompts: [],
  }

  await nextTick()

  if (process.client) {
    provideContainerSize({
      width,
      height,
      isSmall,
    })
  }

  if (process.client) {
    const mainScrollElement = document.querySelector('#main-scroll') as HTMLElement
    if (mainScrollElement) {
      try {
        useInfiniteScroll(
          mainScrollElement,
          () => {
            return layoutScrollHandler()
          }, {
            distance: DISTANCE_TO_LOAD_MORE,
          })
      }
      catch (error) {
        console.warn('Failed to setup infinite scroll:', error)
      }
    }
    else {
      console.warn('main-scroll element not found')
    }
  }

  // Carregar dados iniciais apenas no cliente
  if (process.client && homeList.value.length === 0)
    loadHomeList()

  hasLoadedFeatured.value = true
})

// Carregar dados apenas durante SSR
if (process.server)
  await loadHomeList()
</script>

<template>
  <div ref="containerRef" class="flex flex-col">
    <div>
      <HomeBanner v-if="route.path === '/'" />
      <TopicList />
    </div>
    <div v-if="route.path === '/'" class="max-w-screen overflow-x-auto whitespace-nowrap -mx-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden mx-a max-w-[100vw]  md:max-w-[1400px]">
      <div class="flex gap-3 p-15">
        <NuxtLink to="?dial_id=knowledge-base">
          <CardCTA
            is="button"
            type="button"
            tag-type="tables"
            subtitle="Create a"
            main-text="Knowledge<br/>Base"
            hover-class="hover:shadow-[0_0_0_1px_rgba(125,221,146,1),0_0_40px_0_rgba(0,0,0,0.12)] hover:border-green"
          />
        </NuxtLink>
        <NuxtLink to="?dial_id=buy-workflow">
          <CardCTA
            is="button"
            type="button"
            tag-type="explore"
            subtitle="Buy"
            main-text="Automated<br/>Workflows"
            hover-class="hover:shadow-[0_0_0_1px_rgba(169,147,255,1),0_0_40px_0_rgba(0,0,0,0.12)] hover:border-purple"
          />
        </NuxtLink>
        <NuxtLink to="?dial_id=sell-workflow">
          <CardCTA
            is="button"
            type="button"
            tag-type="marketplace"
            subtitle="Sell"
            main-text="Automated<br/>Workflows"
            hover-class="hover:shadow-[0_0_0_1px_rgba(255,136,236,1),0_0_40px_0_rgba(0,0,0,0.12)] hover:border-primary"
          />
        </NuxtLink>

        <NuxtLink to="/feature/agents">
          <CardCTA
            tag-type="comingSoon"
            subtitle="Create"
            main-text="Autonomous<br/>AI Agents"
            hover-class="hover:shadow-[0_0_0_1px_rgba(109,86,237,1),0_0_40px_0_rgba(0,0,0,0.12)] hover:border-blue"
          />
        </NuxtLink>
      </div>
    </div>

    <div
      class="flex flex-col gap-15  mx-auto px-4 md:px-6 lg:px-8 w-full overflow-x-hidden max-w-[100vw]  md:max-w-[1400px]"
      :class="{
        'pt-8 md:pt-15': route.path !== '/',
      }"
    >
      <div v-if="route.path === '/'" class="max-w-screen mx-a">
        <HeadingV2 is="h1" level="4" class="text-center">
          Power Up Your AI Workflow & Automations
        </HeadingV2>

        <div class="flex flex-col md:flex-row center gap-3 md:gap-7">
          <TextV2 class="flex center gap-2 body-14">
            <div i-mdi:check />
            <span>
              <b>35 Million+</b> Prompts & Automations used
            </span>
          </TextV2>

          <TextV2 class="hidden md:flex center gap-2 body-14">
            <div i-mdi:check />
            <span>
              Trusted by <b>Over 220K</b> Creators
            </span>
          </TextV2>
        </div>
      </div>

      <ElementalFeed
        v-if="showPromptFeatured" :is-featured="true"
        :elemental-type-id="ElementalTypeEnum.Prompt" label="prompts"
        @explore-action="() => handleExplore('prompts')"
      />

      <ElementalFeed
        v-if="showPromptVisualFeatured" :is-featured="true"
        :elemental-type-id="ElementalTypeEnum.Image" label="AI Images & Prompts"
        @explore-action="() => handleExplore('visual-prompts')"
      />

      <ElementalFeed
        v-if="showAutomationFeatured" :is-featured="true" :elemental-type-id="ElementalTypeEnum.Automation"
        label="automations"
        :hidde-topic="false"
        @explore-action="() => handleExplore('automations')"
      />
    </div>
    <CompaniesSection v-if="route.path === '/'" />
    <div
      class="flex flex-col gap-15  mx-auto px-4 md:px-6 lg:px-8 w-full overflow-x-hidden max-w-[100vw]  md:max-w-[1400px]"
      :class="{
        'mt-8 md:mt-15': route.path !== '/',
      }"
    >
      <ElementalMainFeed
        :can-load-mode="canLoadMore"
        :mode="selectedView"
        :is-loading="isLoading"
        :view-settings="viewSettings"
        :elemental-preset="elementalTypes?.filter(t => AVAILABLE_TYPES.includes(t.id))"
        :elemental-types="selectedElementalTypes"
        :items="homeList"
        @toggle-view="handleLayout"
      />
    </div>
  </div>
</template>
